/**
 * GovernanceActivityFeed — Reusable governance timeline visualization
 *
 * Shows governance events (grants, revocations, recertifications, deputy
 * assignments, access requests, membership changes) as a real-time timeline.
 *
 * Three perspectives:
 *   1. Object — filter by collection/app/role to see its governance history
 *   2. Actor  — filter by person to see what they requested / approved / received
 *   3. Global — all events across the tenant (dashboard mode)
 *
 * Usage from any plugin:
 *   import { GovernanceActivityFeed } from '@experience/shared-ui-components';
 *   <GovernanceActivityFeed objectId="coll:ops" objectType="collection" />
 *
 * Blue Steel Patterns applied:
 *   Pattern 10 — Dashboard Loading Excellence (skeleton + staggered reveal)
 *   Pattern 11 — Error State Excellence
 *   Pattern 7  — World-Class Table Selection
 */

const React = (globalThis as any).React;
const useState: <T>(init: T | (() => T)) => [T, (v: T | ((prev: T) => T)) => void] = React.useState;
const useEffect: (effect: () => void | (() => void), deps?: any[]) => void = React.useEffect;
const useCallback: <T extends (...args: any[]) => any>(cb: T, deps: any[]) => T = React.useCallback;
const useRef: <T>(init: T) => { current: T } = React.useRef;

const { usePlugin } = (globalThis as any).PluginSDK || {};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

import {
  formatFriendlyDateTime,
  formatRelativeTime,
  parseArn,
  getActorTypeIcon,
  getActorTypeLabel,
  extractNameFromArn,
  cleanDetailText,
} from './governance-utils';

// ============================================================================
// Types
// ============================================================================

export type GovernanceEventKind =
  | 'grant_created'
  | 'grant_revoked'
  | 'member_added'
  | 'member_removed'
  | 'deputy_assigned'
  | 'deputy_removed'
  | 'access_requested'
  | 'access_approved'
  | 'access_denied'
  | 'access_request_approved'
  | 'access_request_denied'
  | 'recertification_started'
  | 'recertification_completed'
  | 'collection_created'
  | 'collection_deleted'
  | 'collection_exported'
  | 'collection_import_preflighted'
  | 'collection_import_draft_created'
  | 'collection_import_activated'
  | 'collection_import_discarded'
  | 'rule_created'
  | 'policy_changed'
  | 'role_assignment_added'
  | 'role_assignment_removed'
  | 'identity_created'
  | 'identity_updated'
  | 'account_created'
  | 'account_updated'
  | 'account_provisioned'
  | 'task_created'
  | 'task_approved'
  | 'task_rejected'
  | 'delegation_created'
  | 'delegation_revoked'
  | 'agent_exported'
  | 'agent_imported'
  | 'sentinel_exported'
  | 'sentinel_imported'
  | 'dge.safeguard.breach'
  | 'dge.safeguard.override'
  | 'dge.safeguard.cancelled'
  | 'secret_disclosed'
  | 'secret_accessed'
  | 'config_changed'
  | 'config_created'
  | 'config_updated'
  | 'config_deleted'
  | 'config_rotated'
  | 'config_published'
  | 'conformance_run_started'
  | 'conformance_scene_observed'
  | 'conformance_run_verified'
  | 'conformance_tamper_rejected'
  | 'conformance_capsule_exported';

export interface GovernanceEvent {
  id: string;
  kind: GovernanceEventKind;
  timestamp: string;
  actor_arn?: string;
  actor_name?: string;
  target_type?: string;
  target_id?: string;
  target_name?: string;
  subject_arn?: string;
  subject_name?: string;
  detail?: string;
  correlation_id?: string;
  proof_bundle_id?: string;
  action_id?: string;
  provenance_status?: string;
}

export interface GovernanceActivityFeedProps {
  /** Filter events by object (collection, app, role, identity, etc.) */
  objectId?: string;
  objectType?: string;
  /** Filter events by actor ARN */
  actorArn?: string;
  /** Filter events by correlation ID (closed-loop access-request spine) */
  correlationId?: string;
  /** Max events to display */
  limit?: number;
  /** API base URL */
  apiBaseUrl?: string;
  /** Title override */
  title?: string;
  /** Compact mode for embedding in sidebars */
  compact?: boolean;
  /** Accent color CSS variable */
  accentVar?: string;
  /** Optional fetch override for custom auth, SSR, or testing */
  fetchFn?: typeof globalThis.fetch;
  /** Callback when user clicks a proof bundle badge — receives the bundle UUID */
  onProofBundleClick?: (bundleId: string) => void;
}

// ============================================================================
// Event metadata for rendering
// ============================================================================

const EVENT_META: Record<GovernanceEventKind, { icon: string; label: string; color: string; bgVar: string }> = {
  grant_created:              { icon: '\uD83D\uDD11', label: 'Grant Created',         color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  grant_revoked:              { icon: '\uD83D\uDEAB', label: 'Grant Revoked',         color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  member_added:               { icon: '\uD83D\uDC64', label: 'Member Added',          color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  member_removed:             { icon: '\uD83D\uDC64', label: 'Member Removed',        color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  deputy_assigned:            { icon: '\uD83D\uDEE1\uFE0F', label: 'Deputy Assigned',      color: 'var(--main-accent)',   bgVar: 'color-mix(in srgb, var(--main-accent) 12%, transparent)' },
  deputy_removed:             { icon: '\uD83D\uDEE1\uFE0F', label: 'Deputy Removed',       color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  access_requested:           { icon: '\u270B', label: 'Access Requested',      color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  access_approved:            { icon: '\u2705', label: 'Access Approved',       color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  access_denied:              { icon: '\u274C', label: 'Access Denied',         color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  access_request_approved:    { icon: '\u2705', label: 'Request Approved',      color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  access_request_denied:      { icon: '\u274C', label: 'Request Denied',        color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  recertification_started:    { icon: '\uD83D\uDD04', label: 'Recertification Started', color: 'var(--info-color)', bgVar: 'var(--info-bg)' },
  recertification_completed:  { icon: '\u2705', label: 'Recertification Done',  color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  collection_created:         { icon: '\uD83D\uDCC2', label: 'Collection Created',    color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  collection_deleted:         { icon: '\uD83D\uDDD1\uFE0F', label: 'Collection Deleted',   color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  collection_exported:        { icon: '\uD83D\uDCE4', label: 'Collection Exported',   color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  collection_import_preflighted: { icon: '\uD83D\uDD0D', label: 'Import Preflighted', color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  collection_import_draft_created: { icon: '\uD83D\uDCE5', label: 'Import Draft Created', color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  collection_import_activated: { icon: '\u2705', label: 'Import Activated',     color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  collection_import_discarded: { icon: '\uD83D\uDDD1\uFE0F', label: 'Import Discarded',    color: 'var(--muted-color)',   bgVar: 'var(--muted-bg, color-mix(in srgb, var(--main-text) 8%, transparent))' },
  rule_created:               { icon: '\uD83D\uDCDC', label: 'Rule Created',          color: 'var(--main-accent)',   bgVar: 'color-mix(in srgb, var(--main-accent) 12%, transparent)' },
  policy_changed:             { icon: '\uD83D\uDEE1\uFE0F', label: 'Policy Changed',       color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  role_assignment_added:      { icon: '\uD83C\uDFAF', label: 'Role Assigned',         color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  role_assignment_removed:    { icon: '\uD83C\uDFAF', label: 'Role Removed',          color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  identity_created:           { icon: '\uD83D\uDC64', label: 'Identity Created',      color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  identity_updated:           { icon: '\uD83D\uDC64', label: 'Identity Updated',      color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  account_created:            { icon: '\uD83D\uDD11', label: 'Account Created',       color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  account_updated:            { icon: '\uD83D\uDD11', label: 'Account Updated',       color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  account_provisioned:        { icon: '\uD83D\uDD11', label: 'Account Provisioned',   color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  task_created:               { icon: '\uD83D\uDCCB', label: 'Task Created',          color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  task_approved:              { icon: '\u2705', label: 'Task Approved',         color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  task_rejected:              { icon: '\u274C', label: 'Task Rejected',         color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  delegation_created:         { icon: '\uD83E\uDD1D', label: 'Delegation Created',    color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  delegation_revoked:         { icon: '\uD83E\uDD1D', label: 'Delegation Revoked',    color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  agent_exported:             { icon: '\uD83E\uDD16', label: 'Agent Exported',        color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  agent_imported:             { icon: '\uD83E\uDD16', label: 'Agent Imported',        color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  sentinel_exported:          { icon: '\uD83D\uDEE1\uFE0F', label: 'Sentinel Exported',    color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  sentinel_imported:          { icon: '\uD83D\uDEE1\uFE0F', label: 'Sentinel Imported',    color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  'dge.safeguard.breach':     { icon: '\u26A0\uFE0F', label: 'Safeguard Breach',     color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  'dge.safeguard.override':   { icon: '\u2705', label: 'Safeguard Override',   color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  'dge.safeguard.cancelled':  { icon: '\u23F9\uFE0F', label: 'Sync Cancelled',      color: 'var(--muted-color)',   bgVar: 'var(--muted-bg)' },
  secret_disclosed:           { icon: '\uD83D\uDD13', label: 'Secret Disclosed',     color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  secret_accessed:            { icon: '\uD83D\uDD10', label: 'Secret Accessed',      color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  config_changed:             { icon: '\u2699\uFE0F', label: 'Config Changed',       color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  config_created:             { icon: '\u2699\uFE0F', label: 'Config Created',       color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  config_updated:             { icon: '\u2699\uFE0F', label: 'Config Updated',       color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  config_deleted:             { icon: '\uD83D\uDDD1\uFE0F', label: 'Config Deleted',       color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  config_rotated:             { icon: '\uD83D\uDD01', label: 'Config Rotated',       color: 'var(--warning-color)', bgVar: 'var(--warning-bg)' },
  config_published:           { icon: '\uD83D\uDCE4', label: 'Config Published',     color: 'var(--main-accent)',   bgVar: 'color-mix(in srgb, var(--main-accent) 12%, transparent)' },
  conformance_run_started:    { icon: '\u25B6\uFE0F', label: 'Proof Run Started',    color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
  conformance_scene_observed: { icon: '\uD83D\uDC41\uFE0F', label: 'Scene Observed',       color: 'var(--main-accent)',   bgVar: 'color-mix(in srgb, var(--main-accent) 12%, transparent)' },
  conformance_run_verified:   { icon: '\u2705', label: 'Proof Verified',        color: 'var(--success-color)', bgVar: 'var(--success-bg)' },
  conformance_tamper_rejected:{ icon: '\uD83D\uDEE1\uFE0F', label: 'Tamper Rejected',      color: 'var(--error-color)',   bgVar: 'var(--error-bg)' },
  conformance_capsule_exported:{ icon: '\uD83D\uDCE6', label: 'Capsule Exported',     color: 'var(--info-color)',    bgVar: 'var(--info-bg)' },
};

// ============================================================================
// Magnum animation constants
// ============================================================================

const MAGNUM_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// Component
// ============================================================================

export function GovernanceActivityFeed({
  objectId,
  objectType,
  actorArn,
  correlationId,
  limit = 50,
  apiBaseUrl = '/api/v1',
  title,
  compact = false,
  accentVar = 'var(--main-accent)',
  fetchFn,
  onProofBundleClick,
}: GovernanceActivityFeedProps) {
  const sdkApi = usePlugin?.()?.sdk?.api ?? null;
  const [events, setEvents] = useState<GovernanceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [filter, setFilter] = useState<GovernanceEventKind | 'all'>('all');
  const mountedRef = useRef(true);

  // Resolve the fetch function: explicit prop > SDK > globalThis
  const resolveFetch = useCallback((): typeof globalThis.fetch => {
    if (fetchFn) return fetchFn;
    if (sdkApi?.fetch) return sdkApi.fetch;
    return globalThis.fetch.bind(globalThis);
  }, [fetchFn, sdkApi]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (objectId) params.set('object_id', objectId);
      if (objectType) params.set('object_type', objectType);
      if (actorArn) params.set('actor_arn', actorArn);
      if (correlationId) params.set('correlation_id', correlationId);
      params.set('limit', String(limit));

      const url = `${apiBaseUrl}/governance/events?${params}`;
      const doFetch = resolveFetch();
      const response = await doFetch(url, {
        method: 'GET',
        credentials: 'include' as RequestCredentials,
        headers: { 'Content-Type': 'application/json' },
      });

      let data: any;
      if (response.ok) {
        data = await response.json();
      } else if (response.status === 404) {
        data = [];
      } else {
        throw new Error(`HTTP ${response.status}`);
      }

      if (mountedRef.current) {
        setEvents(Array.isArray(data) ? data : data?.items || []);
      }
    } catch (err: any) {
      const correlationId =
        err?.response?.headers?.['x-correlation-id'] ||
        err?.response?.data?.correlation_id ||
        err?.correlationId;

      sdkApi?.logger?.error?.('governance.events_fetch_failed', {
        correlationId,
        message: err?.message,
        status: err?.response?.status,
        objectId,
        objectType,
      });

      if (mountedRef.current) {
        setError(correlationId ? `${err.message} (CID: ${correlationId.slice(0, 8)})` : err.message);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        requestAnimationFrame(() => { if (mountedRef.current) setVisible(true); });
      }
    }
  }, [apiBaseUrl, objectId, objectType, actorArn, correlationId, limit, resolveFetch]);

  useEffect(() => {
    mountedRef.current = true;
    fetchEvents();
    return () => { mountedRef.current = false; };
  }, [fetchEvents]);

  const filteredEvents = filter === 'all' ? events : events.filter((e: GovernanceEvent) => e.kind === filter);

  const displayTitle = title
    || (objectId ? 'Governance History' : actorArn ? 'My Governance Activity' : 'Governance Activity Feed');

  // ── Loading skeleton ──────────────────────────────────────────────
  if (loading) {
    return React.createElement('div', {
      'data-testid': 'gov-feed-loading',
      style: { padding: compact ? '12px' : '24px' },
    },
      React.createElement('div', {
        style: {
          display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px',
        },
      },
        React.createElement('div', {
          style: {
            width: '12px', height: '12px', borderRadius: '50%',
            border: `2px solid ${accentVar}`, borderTopColor: 'transparent',
            animation: 'gov-feed-spin 1s linear infinite',
          },
        }),
        React.createElement('span', {
          style: { fontSize: '13px', color: 'var(--main-text-secondary)' },
        }, 'Loading governance events\u2026'),
      ),
      ...Array.from({ length: 5 }).map((_, i) =>
        React.createElement('div', {
          key: i,
          style: {
            height: compact ? '36px' : '52px',
            backgroundColor: 'var(--skeleton-bg)',
            borderRadius: '8px',
            marginBottom: '8px',
            animation: `gov-feed-pulse 1.5s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
          },
        }),
      ),
      React.createElement('style', null, `
        @keyframes gov-feed-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes gov-feed-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `),
    );
  }

  // ── Error state (Pattern 11) ──────────────────────────────────────
  if (error) {
    return React.createElement('div', {
      'data-testid': 'gov-feed-error',
      style: {
        padding: compact ? '16px' : '32px', textAlign: 'center',
        background: 'var(--error-bg)',
        borderRadius: '12px', border: '1px solid var(--error-color)',
      },
    },
      React.createElement('div', { style: { fontSize: '32px', marginBottom: '8px' } }, '\u26A0\uFE0F'),
      React.createElement('div', {
        style: { fontSize: '14px', fontWeight: 600, color: 'var(--main-text)', marginBottom: '4px' },
      }, 'Unable to load governance events'),
      React.createElement('div', {
        style: { fontSize: '12px', color: 'var(--main-text-secondary)', marginBottom: '12px' },
      }, error),
      React.createElement('button', {
        'data-testid': 'gov-feed-retry',
        onClick: fetchEvents,
        style: {
          padding: '8px 20px', fontSize: '13px', fontWeight: 600,
          borderRadius: '8px', border: 'none', cursor: 'pointer',
          background: accentVar, color: 'var(--button-primary-text)',
        },
      }, 'Retry'),
    );
  }

  // ── Main feed ─────────────────────────────────────────────────────
  return React.createElement('div', {
    'data-testid': 'gov-feed',
    style: {
      padding: 0,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
    // Header
    React.createElement('div', {
      style: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: compact ? '12px' : '20px',
      },
    },
      React.createElement('h3', {
        style: {
          fontSize: compact ? '15px' : '18px', fontWeight: 700,
          color: 'var(--main-text)', margin: 0,
          display: 'flex', alignItems: 'center', gap: '8px',
        },
      },
        React.createElement('span', {
          style: {
            width: '8px', height: '8px', borderRadius: '50%',
            background: accentVar, display: 'inline-block',
            boxShadow: `0 0 8px ${accentVar}`,
            animation: 'governance-pulse 2s ease-in-out infinite',
          },
        }),
        displayTitle,
        events.length > 0 && React.createElement('span', {
          style: {
            fontSize: '12px', fontWeight: 600, color: 'var(--main-text-secondary)',
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '10px', padding: '2px 8px',
            fontFamily: "'SF Mono', 'Cascadia Code', monospace",
          },
        }, String(filteredEvents.length)),
      ),

      // Filter chips
      !compact && React.createElement('div', {
        'data-testid': 'gov-feed-filters',
        style: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
      },
        ...(['all', 'grant_created', 'grant_revoked', 'member_added', 'access_requested', 'deputy_assigned', 'recertification_started', 'dge.safeguard.breach'] as const).map((f: typeof filter) => {
          const isActive = filter === f;
          const meta = f === 'all' ? { icon: '\uD83D\uDCCB', label: 'All' } : EVENT_META[f];
          return React.createElement('button', {
            key: f,
            'data-testid': `gov-feed-filter-${f}`,
            'aria-label': `Filter by ${meta.label}`,
            'aria-pressed': isActive,
            onClick: () => setFilter(f),
            style: {
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', fontSize: '11px', fontWeight: isActive ? 700 : 500,
              borderRadius: '16px', border: 'none', cursor: 'pointer',
              background: isActive
                ? `color-mix(in srgb, ${accentVar} 15%, transparent)`
                : 'var(--card-bg)',
              color: isActive ? accentVar : 'var(--main-text-secondary)',
              transition: `all 0.2s ${MAGNUM_EASING}`,
            },
          },
            React.createElement('span', { style: { fontSize: '12px' } }, meta.icon),
            meta.label,
          );
        }),
      ),
    ),

    // Empty state
    filteredEvents.length === 0 && React.createElement('div', {
      'data-testid': 'gov-feed-empty',
      style: {
        textAlign: 'center', padding: compact ? '24px' : '48px',
        color: 'var(--main-text-secondary)',
      },
    },
      React.createElement('div', { style: { fontSize: '36px', marginBottom: '12px', opacity: 0.5 } }, '\uD83D\uDD12'),
      React.createElement('div', { style: { fontSize: '14px', fontWeight: 500 } },
        'No governance events yet',
      ),
      React.createElement('div', { style: { fontSize: '12px', marginTop: '4px' } },
        objectId
          ? 'Activity for this object will appear here as it happens.'
          : 'Governance events (grants, revocations, approvals) will appear here.',
      ),
    ),

    // Timeline
    filteredEvents.length > 0 && React.createElement('div', {
      'data-testid': 'gov-feed-timeline',
      style: {
        position: 'relative',
        paddingLeft: compact ? '24px' : '32px',
      },
    },
      // Vertical timeline line
      React.createElement('div', {
        style: {
          position: 'absolute',
          left: compact ? '10px' : '14px',
          top: '4px',
          bottom: '4px',
          width: '2px',
          background: `linear-gradient(to bottom, ${accentVar}, var(--border-color))`,
          borderRadius: '1px',
        },
      }),

      // Event items
      ...filteredEvents.map((event: GovernanceEvent, idx: number) => {
        const meta = EVENT_META[event.kind as GovernanceEventKind] || EVENT_META.policy_changed;
        return React.createElement('div', {
          key: event.id,
          'data-testid': `gov-feed-event-${event.id}`,
          style: {
            position: 'relative',
            padding: compact ? '8px 0' : '12px 0',
            animation: `magnum-event-enter 0.3s ${MAGNUM_EASING} ${Math.min(idx * 50, 500)}ms both`,
          },
        },
          // Timeline dot
          React.createElement('div', {
            style: {
              position: 'absolute',
              left: compact ? '-20px' : '-26px',
              top: compact ? '12px' : '16px',
              width: compact ? '12px' : '16px',
              height: compact ? '12px' : '16px',
              borderRadius: '50%',
              background: meta.bgVar,
              border: `2px solid ${meta.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: compact ? '7px' : '9px',
              zIndex: 1,
            },
          }, meta.icon),

          // Event card
          React.createElement('div', {
            style: {
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: compact ? '8px' : '12px',
              padding: compact ? '10px 12px' : '14px 18px',
              boxShadow: 'var(--card-shadow)',
              transition: `all 0.2s ${MAGNUM_EASING}`,
              cursor: 'default',
            },
            onMouseEnter: (e: any) => {
              e.currentTarget.style.borderColor = meta.color;
              e.currentTarget.style.boxShadow = `0 2px 12px color-mix(in srgb, ${meta.color} 15%, transparent)`;
            },
            onMouseLeave: (e: any) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'var(--card-shadow)';
            },
          },
            // Header row
            React.createElement('div', {
              style: {
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '8px', marginBottom: compact ? '2px' : '6px',
              },
            },
              React.createElement('span', {
                style: {
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  fontSize: compact ? '12px' : '13px', fontWeight: 600,
                  color: meta.color,
                },
              },
                React.createElement('span', { style: { fontSize: compact ? '14px' : '16px' } }, meta.icon),
                meta.label,
              ),
              // Timestamp and Journey link container
              React.createElement('div', {
                style: { display: 'flex', alignItems: 'center', gap: '12px' },
              },
                (() => {
                  const displayDate = formatFriendlyDateTime(event.timestamp);
                  const tooltipDate = formatRelativeTime(event.timestamp);
                  return React.createElement('span', {
                    style: {
                      fontSize: '11px', color: 'var(--main-text-secondary)',
                      whiteSpace: 'nowrap',
                      cursor: 'help',
                    },
                    title: `Relative: ${tooltipDate}`,
                  }, displayDate);
                })(),

                // View Journey link (if subject exists)
                event.subject_arn && React.createElement('a', {
                  href: `/identity-journeys/${encodeURIComponent(event.subject_arn)}`,
                  style: {
                    fontSize: '11px',
                    color: 'var(--main-accent)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: 'color-mix(in srgb, var(--main-accent) 10%, transparent)',
                    transition: 'all 0.2s',
                  },
                  title: `View full identity journey for ${event.subject_arn}`,
                  onMouseEnter: (e: any) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 20%, transparent)';
                  },
                  onMouseLeave: (e: any) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 10%, transparent)';
                  },
                }, '\uD83D\uDD0D Journey'),
              ),
            ),

            // Detail — what happened and to whom
            (() => {
              const subjectName = event.subject_name || extractNameFromArn(event.subject_arn);
              const hasSubject = Boolean(subjectName);
              const hasDetail = Boolean(event.detail);

              if (!hasSubject && !hasDetail) return null;

              const subjectArnParsed = parseArn(event.subject_arn);
              const targetArnParsed = parseArn(event.target_id);
              const actionText = cleanDetailText(event.detail, event.subject_arn);

              return React.createElement('div', {
                style: {
                  fontSize: compact ? '12px' : '13px',
                  color: 'var(--main-text)',
                  lineHeight: 1.5,
                },
              },
                hasSubject && React.createElement('div', { style: { marginBottom: '4px' } },
                  React.createElement('span', { style: { color: 'var(--main-text-secondary)' } },
                    actionText || meta.label
                  ),
                  React.createElement('span', { style: { margin: '0 4px', color: 'var(--main-text-secondary)' } }, ':'),
                  React.createElement('strong', { style: { color: 'var(--main-text)' } }, subjectName),
                  subjectArnParsed && React.createElement('span', {
                    style: {
                      fontSize: '11px',
                      color: 'var(--main-text-secondary)',
                      marginLeft: '6px',
                      fontFamily: "'SF Mono', monospace",
                    },
                  }, `(${subjectArnParsed.full})`)
                ),

                targetArnParsed && React.createElement('div', {
                  style: {
                    fontSize: '11px',
                    color: 'var(--main-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  },
                },
                  React.createElement('span', null, '\uD83D\uDCC1'),
                  React.createElement('span', null, `${event.target_type || 'Target'}: `),
                  React.createElement('strong', null, event.target_name || targetArnParsed.name),
                ),

                !hasSubject && (event.detail || meta.label)
              );
            })(),

            // Actor + proof bundle row
            !compact && (() => {
              const actorArnParsed = parseArn(event.actor_arn);
              const hasActor = Boolean(actorArnParsed || event.actor_name);
              const hasProof = Boolean(event.proof_bundle_id) && UUID_RE.test(event.proof_bundle_id!);
              const hasCorrelation = Boolean(event.correlation_id);

              if (!hasActor && !hasProof && !hasCorrelation) return null;

              const actorType = actorArnParsed?.type || 'unknown';
              const actorName = event.actor_name || actorArnParsed?.name || 'Unknown';

              return React.createElement('div', {
                style: {
                  display: 'flex', alignItems: 'center', gap: '12px',
                  marginTop: '8px', paddingTop: '8px',
                  borderTop: '1px solid var(--border-color)',
                  fontSize: '11px', color: 'var(--main-text-secondary)',
                  flexWrap: 'wrap',
                },
              },
                hasActor && React.createElement('span', {
                  style: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
                },
                  React.createElement('span', null, getActorTypeIcon(actorType)),
                  React.createElement('span', { style: { fontWeight: 600 } },
                    `${getActorTypeLabel(actorType)}: `
                  ),
                  React.createElement('strong', { style: { color: 'var(--main-text)' } }, actorName),
                  actorArnParsed && React.createElement('span', {
                    style: {
                      fontSize: '10px',
                      color: 'var(--main-text-secondary)',
                      marginLeft: '4px',
                      fontFamily: "'SF Mono', monospace",
                      opacity: 0.8,
                    },
                    title: `Full ARN: ${actorArnParsed.full}`,
                  }, `(${actorArnParsed.full})`),
                ),

                hasCorrelation && React.createElement('span', {
                  style: {
                    fontFamily: "'SF Mono', monospace",
                    fontSize: '10px',
                    opacity: 0.7,
                    cursor: 'help',
                  },
                  title: `Correlation ID: ${event.correlation_id}`,
                }, `Trace: ${event.correlation_id!.slice(0, 8)}\u2026`),

                hasProof && (onProofBundleClick
                  ? React.createElement('button', {
                    onClick: () => onProofBundleClick(event.proof_bundle_id!),
                    style: {
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      background: 'color-mix(in srgb, var(--main-accent) 8%, transparent)',
                      padding: '2px 8px', borderRadius: '10px',
                      fontFamily: "'SF Mono', monospace", fontSize: '10px',
                      cursor: 'pointer', color: 'inherit', border: 'none',
                      transition: 'background 0.2s',
                    },
                    title: `Download Proof Bundle: ${event.proof_bundle_id}`,
                    onMouseEnter: (e: any) => {
                      e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 15%, transparent)';
                    },
                    onMouseLeave: (e: any) => {
                      e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 8%, transparent)';
                    },
                  },
                    '\uD83D\uDCE6 Audit: ',
                    event.proof_bundle_id!.slice(0, 8) + '\u2026',
                  )
                  : React.createElement('a', {
                  href: `/api/crud/proof/bundles/${event.proof_bundle_id}`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  style: {
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: 'color-mix(in srgb, var(--main-accent) 8%, transparent)',
                    padding: '2px 8px', borderRadius: '10px',
                    fontFamily: "'SF Mono', monospace", fontSize: '10px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'background 0.2s',
                  },
                  title: `View Proof Bundle: ${event.proof_bundle_id}`,
                  onMouseEnter: (e: any) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 15%, transparent)';
                  },
                  onMouseLeave: (e: any) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--main-accent) 8%, transparent)';
                  },
                },
                  '\uD83D\uDCE6 Audit: ',
                  event.proof_bundle_id!.slice(0, 8) + '\u2026',
                )),
              );
            })(),
          ),
        );
      }),
    ),

    // Keyframe styles — injected once via DOM guard
    React.createElement(GovFeedStyles, null),
  );
}

/** Injects keyframe styles once via DOM guard — prevents re-injection on every render */
function GovFeedStyles() {
  useEffect(function () {
    const id = 'gov-feed-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes magnum-event-enter {
        from { opacity: 0; transform: translateX(-10px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes governance-pulse {
        0%, 100% { opacity: 1; box-shadow: 0 0 8px var(--main-accent); }
        50%      { opacity: 0.5; box-shadow: 0 0 16px var(--main-accent); }
      }
      @keyframes gov-feed-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      @keyframes gov-feed-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
}

export default GovernanceActivityFeed;
